import { OrderReferenceNumberField } from "@/services/backend/htz/orders/components/order-reference-number-field";
import { DocumentState } from "@/services/backend/documents/types/state";
import { Channel, Order } from "@/services/backend/htz/orders/types/order";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/shared/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/shared/components/ui/select";
import t from "@/lang/lang";
import { useEffect } from "react";
import {
  PatchRequest,
  updateHtzOrderCache,
  useHtzOrderPatchMutation,
} from "@/services/backend/htz/orders/api/order-api";
import { isImmutable } from "@/services/backend/documents/lib/document-state";
import { useOptimisticDebouncedMutation } from "@/shared/lib/debounce/use-optimistic-debounced-mutation";
import { TextInputField } from "@/shared/components/field/input-field";
import { TextareaField } from "@/shared/components/field/textarea-field";
import { Label } from "@/shared/components/ui/label";
import { DatePickerField } from "@/shared/components/field/date-picker-field";
import { useHtzOfferListQuery } from "@/services/backend/htz/offers/api/offer-api";
import { ReferenceNumber } from "@/services/backend/documents/components/reference-number";
import { useCpyCopyMutation } from "@/services/backend/cpy/api/cpy";
import { CpyName } from "@/services/backend/cpy/types/entity-names";
import { parseRTKQueryErrorToSonner } from "@/shared/components/errors/parse-r-t-k-query-error";
import { toast } from "sonner";
import { DocumentStateBadge } from "@/shared/bsm/core/document/components/ui/document-state-badge";

/**
 * OrderCard edits the data of the order and saves it as it is changed.
 */
export function OrderCard({ order }: { order: Order }) {
  const request: PatchRequest = { id: order.id };
  const [set] = useOptimisticDebouncedMutation(
    request,
    updateHtzOrderCache,
    useHtzOrderPatchMutation(),
  );
  const disabled = isImmutable(order);

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex">
          <span>{t("Auftragseingang")}</span>
        </CardTitle>
      </CardHeader>
      <CardContent className="grid gap-4 md:grid-cols-2">
        <OrderReferenceNumberField orderId={order.id} />
        <OfferSelect order={order} disabled={disabled} />
        <DatePickerField
          label={t("Eingangsdatum")}
          nullable={true}
          date={order.date ?? undefined}
          onDateChange={(date) =>
            set(
              { ...request, date: date ?? null },
              { ...order, date: date ?? null },
            )
          }
          fieldName="date"
          fieldProps={{ className: "content-start" }}
          disabled={disabled}
        />
        <ChannelSelect
          channel={order.channel}
          onChannelChange={(channel) =>
            set({ ...request, channel }, { ...order, channel })
          }
          disabled={disabled}
        />
        <TextInputField
          label={t("Referenz des Kunden")}
          description={t("z.B. Bestellnummer, Auftragsnummer")}
          text={order.customerReference}
          onTextChange={(customerReference) =>
            set(
              { ...request, customerReference },
              { ...order, customerReference },
            )
          }
          fieldName="customerReference"
          fieldProps={{ className: "content-start" }}
          disabled={disabled}
        />
        <TextareaField
          label={t("Interner Kommentar")}
          value={order.internalComment}
          onChange={(e) =>
            set(
              { ...request, internalComment: e.target.value },
              { ...order, internalComment: e.target.value },
            )
          }
          fieldName="internalComment"
          fieldProps={{ className: "md:col-span-2" }}
          disabled={disabled}
        />
      </CardContent>
    </Card>
  );
}

export function OrderCardSkeleton() {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex">
          <span>{t("Auftragseingang")}</span>
        </CardTitle>
      </CardHeader>
    </Card>
  );
}

function OfferSelect({ order, disabled }: { order: Order; disabled: boolean }) {
  const {
    data: list,
    isLoading,
    error,
  } = useHtzOfferListQuery({
    process: {
      active: order.processId !== null,
      values: [order.processId ?? "00000000-0000-0000-0000-000000000000"],
    },
    state: {
      active: true,
      values: [DocumentState.Immutable],
    },
  });

  // We re-use the copy service to update the order.
  const [
    cpy,
    {
      isLoading: cpyIsLoading,
      error: cpyError,
      isSuccess: cpyIsSuccess,
      reset: cpyReset,
    },
  ] = useCpyCopyMutation();

  const doCpy = (offerId: string) => {
    if (cpyIsLoading) return;

    cpy({
      destinationEntityName: CpyName.HTZOrder,
      destinationId: order.id,
      sourceEntityName: CpyName.HTZOffer,
      sourceId: offerId,
    });
  };

  useEffect(() => {
    if (error) {
      toast(...parseRTKQueryErrorToSonner(error));
    }
    if (cpyError) {
      toast(...parseRTKQueryErrorToSonner(cpyError));
      cpyReset();
    }
  }, [cpyError, cpyReset, error]);

  useEffect(() => {
    if (cpyIsSuccess) {
      toast.success(t("Daten übernommen."));
    }
  });

  return (
    <div className="grid content-start gap-1.5">
      <Label>{t("Zu Angebot")}</Label>
      <Select
        value={order.offerId ?? undefined}
        onValueChange={doCpy}
        disabled={disabled || !list || isLoading || cpyIsLoading}
      >
        <SelectTrigger className="w-full">
          <SelectValue placeholder={t("Angebot auswählen")} />
        </SelectTrigger>
        <SelectContent>
          {list?.items.map((offer) => (
            <SelectItem value={offer.id} key={offer.id}>
              <span className="flex items-center gap-2">
                <ReferenceNumber referenceNumber={offer.referenceNumber} />
                <DocumentStateBadge document={offer} />
              </span>
            </SelectItem>
          ))}
          <SelectItem value="00000000-0000-0000-0000-000000000000">
            {t("Kein Angebot")}
          </SelectItem>
        </SelectContent>
      </Select>
      <div className="text-muted-foreground text-xs">
        {t(
          "Die Auswahl eines Angebots überschreibt die Leistungen des Auftragseingangs mit denen des Angebots.",
        )}
      </div>
    </div>
  );
}

interface ChannelSelectProps {
  channel: Channel | null;
  onChannelChange: (channel: Channel | null) => void;
  disabled: boolean;
}

function ChannelSelect({
  channel,
  onChannelChange,
  disabled,
}: ChannelSelectProps) {
  const channels = Object.entries(Channel).map((chan) => chan[1]);

  const onValueChange = (value: string | null) => {
    onChannelChange(value === null ? null : (value as Channel));
  };

  return (
    <div className="grid content-start gap-1.5">
      <Label>{t("Kanal")}</Label>
      <Select
        value={channel ?? undefined}
        onValueChange={onValueChange}
        disabled={disabled}
      >
        <SelectTrigger className="w-full">
          <SelectValue placeholder={t("Kanal auswählen")} />
        </SelectTrigger>
        <SelectContent>
          {channels.map((chan) => (
            <SelectItem value={chan} key={chan}>
              {t(chan)}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}
